import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import employeeObject from '@/entities/employee/interface'

import {
  ALL_EMPLOYEES_BLOCKS_QUERY,
  SINGLE_EMPLOYEES_BLOCK_QUERY,
  CREATE_EMPLOYEES_BLOCK_MUTATION,
  UPDATE_EMPLOYEES_BLOCK_MUTATION,
  DELETE_EMPLOYEES_BLOCK_MUTATION,
  FORCE_DELETE_EMPLOYEES_BLOCK_MUTATION,
  ADD_EMPLOYEES_BLOCK_EMPLOYEES_MUTATION,
  REMOVE_EMPLOYEES_BLOCK_EMPLOYEES_MUTATION,
  CREATE_EMPLOYEES_BLOCK_EMPLOYEES_MUTATION,
  DELETE_EMPLOYEES_BLOCK_EMPLOYEES_MUTATION,
  UPDATE_EMPLOYEES_BLOCK_EMPLOYEES_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const cmsEmployeesBlockRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_EMPLOYEES_BLOCK_QUERY, variables).then((data: any) => {
      return hydrateObject(data.employeesBlock)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_EMPLOYEES_BLOCKS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.employeesBlocks)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_EMPLOYEES_BLOCK_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.employeesBlock),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_EMPLOYEES_BLOCKS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.employeesBlocks),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_EMPLOYEES_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.employeesBlock)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_EMPLOYEES_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.employeesBlock)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_EMPLOYEES_BLOCK_MUTATION, variables).then((data: any) => {
      return data.deleteCmsEmployeesBlock
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_EMPLOYEES_BLOCK_MUTATION, variables).then((data: any) => {
      return data.forceDeleteCmsEmployeesBlock
    })
  },

  assignEmployees: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        employeeIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_EMPLOYEES_BLOCK_EMPLOYEES_MUTATION, variables)
  },

  unassignEmployees: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        employeeIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_EMPLOYEES_BLOCK_EMPLOYEES_MUTATION, variables)
  },

  createEmployees: async (id: string, items: Array<employeeObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        employees: items,
      },
    }
    return graphQLClient.request(CREATE_EMPLOYEES_BLOCK_EMPLOYEES_MUTATION, variables)
  },

  deleteEmployees: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        employeeIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_EMPLOYEES_BLOCK_EMPLOYEES_MUTATION, variables)
  },

  updateEmployeesOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        employeeIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_EMPLOYEES_BLOCK_EMPLOYEES_ORDER_MUTATION, variables)
  },
}

export default cmsEmployeesBlockRepository
